import { filterClasses } from "@swivl-mono/helpers";
import { UserInterface } from "@swivl-mono/types";
import { Button, ButtonColor, ButtonSize, ButtonVariant } from "@swivl-mono/ui";
import { AnimatePresence } from "framer-motion";
import { useEffect, useMemo, useRef, useState } from "react";

import useKeyPressAction from "../../../../../Hooks/useKeyPressAction";
import MembersModel from "../../../../../Models/Member/Members.Model";
import NotesModel from "../../../../../Models/NotesMode/Notes.Model";
import UserDetailsNote from "../Components/UserDetails.Note.Component";

interface UserDetailsNotesViewProps {
  user: UserInterface;
}

export function UserDetailsNotesView(props: UserDetailsNotesViewProps) {
  const [noteContent, setNoteContent] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const { user } = props;
  const { notes } = NotesModel.useState((s) => s);
  const { membersMap } = MembersModel.useState((s) => s);

  const sortedNotes = useMemo(() => {
    const userNotes = notes[user.id];
    if (!userNotes) return [];

    return [...userNotes]?.sort((a, b) => {
      const dateA = new Date(a.createdAt || a.updatedAt || "");
      const dateB = new Date(b.createdAt || b.updatedAt || "");
      return dateB.getTime() - dateA.getTime();
    });
  }, [notes, user.id]);

  useKeyPressAction({
    key: "Enter",
    callback: () => {
      handleCreateNote(user.id, noteContent);
      setNoteContent("");
    },
    targetEl: inputRef.current
  });

  useEffect(() => {
    NotesModel.loadForUser(user.id, false);
    MembersModel.loadMembers();
  }, [user.id]);

  const handleCreateNote = (userId: number, content: string) => {
    if (content.trim() === "") return;

    NotesModel.createNoteForUser(userId, content);
  };

  return (
    <div className="flex w-full flex-col gap-3 pb-4">
      <div
        className={filterClasses(
          "flex h-[42px] w-full items-center justify-between rounded-sm p-2 ring ring-grey-400",
          "focus-within:ring-orange-500 focus-within:outline-none hover:ring-orange-500",
          "transition-all duration-200 ease-in-out"
        )}
      >
        <input
          className="w-[calc(100%-40px)] truncate border-0 bg-transparent p-0 pl-1 text-sm outline-none placeholder:text-grey-400"
          ref={inputRef}
          value={noteContent}
          onChange={(e) => setNoteContent(e.target.value)}
          type="text"
          placeholder="Leave a note"
        />
        <Button
          disabled={noteContent.trim() === ""}
          onClick={() => {
            handleCreateNote(user.id, noteContent);
            setNoteContent("");
          }}
          size={ButtonSize.SMALL}
          color={ButtonColor.ORANGE}
          variant={ButtonVariant.ICON}
        >
          +
        </Button>
      </div>
      <div className="flex flex-col gap-3">
        <AnimatePresence initial={false} mode="popLayout">
          {sortedNotes?.length ? (
            sortedNotes.map((note) => <UserDetailsNote key={note.id} note={note} membersMap={membersMap} />)
          ) : (
            <div className="text-sm text-grey-600 italic">No notes available for this user.</div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
